import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, apiUrl } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { CandidateFormDialog } from '@/features/recruitment/CandidateFormDialog'
import {
  CANDIDATE_SOURCES,
  CANDIDATE_SOURCE_LABELS,
  deleteCandidate,
  exportCandidates,
  listCandidates,
  type CandidateFilters,
  type CandidateRead,
  type CandidateSource,
} from '@/features/recruitment/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * Everyone who has ever applied, independent of which posting or stage they sit at --
 * the pipeline view for that is `Applications`. This screen is the candidate record
 * itself: contact details, source, and the résumé on file.
 */
export function Candidates() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<CandidateFilters>({ page: 1 })
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<{ candidate?: CandidateRead } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<CandidateRead | null>(null)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['candidates', filters],
    queryFn: () => listCandidates(filters),
    retry: false,
  })

  const canCreate = can('performance_reviews', 'create')
  const canUpdate = can('performance_reviews', 'update')
  const canDelete = can('performance_reviews', 'delete')

  const remove = useMutation({
    mutationFn: (candidate: CandidateRead) => deleteCandidate(candidate.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['candidates'] })
      toast.success('Candidate removed.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  const candidates = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<CandidateFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<CandidateRead>[] = [
    {
      key: 'full_name',
      header: 'Name',
      className: 'font-medium',
      sortValue: (candidate) => candidate.full_name,
      cell: (candidate) =>
        canUpdate ? (
          <button
            type="button"
            onClick={() => setDialog({ candidate })}
            className="text-left hover:underline"
          >
            {candidate.full_name}
          </button>
        ) : (
          candidate.full_name
        ),
    },
    {
      key: 'email',
      header: 'Email',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (candidate) => candidate.email,
    },
    {
      key: 'phone',
      header: 'Phone',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (candidate) => candidate.phone ?? '—',
    },
    {
      key: 'source',
      header: 'Source',
      cell: (candidate) => (candidate.source ? CANDIDATE_SOURCE_LABELS[candidate.source] : '—'),
    },
    {
      key: 'resume_key',
      header: 'Résumé',
      cell: (candidate) =>
        candidate.resume_key ? (
          candidate.resume_url ? (
            <a
              href={apiUrl(candidate.resume_url)}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="text-sm text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
            >
              {candidate.resume_key.split('/').pop()}
            </a>
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {candidate.resume_key.split('/').pop()}
            </span>
          )
        ) : (
          <span className="text-sm text-slate-400 dark:text-slate-500">None on file</span>
        ),
    },
    ...(canDelete
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: (candidate: CandidateRead) => (
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(candidate)}>
                Delete
              </Button>
            ),
          } satisfies Column<CandidateRead>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Candidates</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} candidate${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setDialog({})}>Add candidate</Button>}
      </header>

      <Card>
        <CardHeader title="Candidate records" />
        <DataTable
          rows={candidates}
          columns={columns}
          getRowId={(candidate) => candidate.id}
          caption="Candidates"
          isLoading={isLoading}
          error={error}
          emptyMessage="No candidates match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="candidate-search">
                Search candidates
              </label>
              <input
                id="candidate-search"
                className={SELECT_CLASS}
                placeholder="Name or email…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') set({ q: search })
                }}
                onBlur={() => set({ q: search })}
              />

              <label className="sr-only" htmlFor="candidate-source">
                Source
              </label>
              <select
                id="candidate-source"
                className={SELECT_CLASS}
                value={filters.source ?? ''}
                onChange={(event) => set({ source: event.target.value as CandidateSource | '' })}
              >
                <option value="">Any source</option>
                {CANDIDATE_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {CANDIDATE_SOURCE_LABELS[source]}
                  </option>
                ))}
              </select>

              {(filters.q || filters.source) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setFilters({ page: 1 })
                  }}
                >
                  Clear
                </Button>
              )}

              <ExportButton
                filename="candidates"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportCandidates(filters, format)}
                errorMessage="Could not export the candidates."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      {dialog && (
        <CandidateFormDialog open candidate={dialog.candidate} onClose={() => setDialog(null)} />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.full_name}?` : 'Delete candidate'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          This is a soft delete: the candidate is hidden from this list, but every past
          application against them stays exactly as it was.
        </Alert>
      </Modal>
    </div>
  )
}
